import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import StudentShell from "../components/StudentShell/StudentShell";
import * as homeworksService from "../services/homeworks";
import ExerciseRunner, { exerciseHasAnswer } from "../components/tests/ExerciseRunner";
import { formatDateTime, isPast } from "../utils/homeworkDates";
import "./ProgramTestTake.css";
import "./HomeworkTake.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function roundScore(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : null;
}

function answersFromSubmission(submission) {
  const map = {};
  asArray(submission?.answers).forEach((a) => {
    map[String(a.exercise_id)] = a.answer;
  });
  return map;
}

export default function HomeworkTake() {
  const { homeworkId } = useParams();
  const { user, accessToken, effectiveRole, activeStudentId } = useAuth();
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";
  const tr = useCallback((key, options) => t(`homeworkUi.${key}`, options), [t]);

  const isStudent = effectiveRole === "student";
  const isParentForChild = user?.role === "parent" && Boolean(activeStudentId);
  const canAnswer = isStudent || isParentForChild;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [homework, setHomework] = useState(null);
  const [answers, setAnswers] = useState({});
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const topRef = useRef(null);

  const draftKey = `casaclass:homeworkAnswers:authed:${String(homeworkId)}`;

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const params = { include_exercises: "true" };
        if (user?.role === "parent" && activeStudentId) params.student_id = activeStudentId;
        const payload = await homeworksService.fetchHomework({ accessToken, homeworkId, params });
        if (ignore) return;
        setHomework(payload);
        if (payload?.submission) {
          setAnswers(answersFromSubmission(payload.submission));
        } else {
          try {
            const saved = JSON.parse(sessionStorage.getItem(draftKey) || "null");
            if (saved && typeof saved === "object") setAnswers(saved);
          } catch {
            // no draft
          }
        }
      } catch (e) {
        if (!ignore) setError(e?.message || "");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    if (accessToken && homeworkId) load();
    else setLoading(false);
    return () => {
      ignore = true;
    };
  }, [accessToken, activeStudentId, draftKey, homeworkId, user?.role]);

  const submission = homework?.submission || null;
  const isReviewed = submission?.status === "reviewed";
  // Answering when nothing was sent yet, or when changing a copy not yet corrected.
  const answering = canAnswer && (!submission || (editing && !isReviewed));

  // Keep an unsent draft in this tab, so a reload doesn't lose the answers.
  useEffect(() => {
    if (!answering || submission) return undefined;
    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(draftKey, JSON.stringify(answers || {}));
      } catch {
        // storage unavailable
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [answers, answering, draftKey, submission]);

  // Warn before leaving with answers not sent yet.
  useEffect(() => {
    if (!answering || !dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [answering, dirty]);

  const exercises = useMemo(
    () => asArray(homework?.exercises).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [homework?.exercises],
  );
  const questions = useMemo(
    () => exercises.filter((e) => String(e?.exercise_type || "") !== "stimulus"),
    [exercises],
  );
  const answeredCount = questions.filter((e) => exerciseHasAnswer(e, answers[String(e.id)])).length;
  const progress = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;
  const numberById = useMemo(() => {
    const map = {};
    questions.forEach((e, i) => {
      map[String(e.id)] = i + 1;
    });
    return map;
  }, [questions]);

  const typeLabel = (type) =>
    t(`testsModule.editor.typeNames.${type}`, {
      defaultValue: t(`testsModule.exerciseTypes.${type}`, { defaultValue: type }),
    });

  const send = async () => {
    const missing = questions.length - answeredCount;
    if (missing > 0 && !window.confirm(tr("take.confirmUnanswered", { count: missing }))) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        answers: exercises.map((ex) => ({ exercise_id: ex.id, answer: answers[String(ex.id)] ?? {} })),
      };
      if (user?.role === "parent" && activeStudentId) payload.student_id = activeStudentId;
      const created = await homeworksService.submitHomework({ accessToken, homeworkId, payload });
      try {
        sessionStorage.removeItem(draftKey);
      } catch {
        // storage unavailable
      }
      setHomework((prev) => ({ ...prev, submission: created }));
      setAnswers(answersFromSubmission(created));
      setEditing(false);
      setDirty(false);
      setJustSent(true);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      setError(e?.message || "");
    } finally {
      setSubmitting(false);
    }
  };

  const score = roundScore(submission?.score);
  const maxScore = roundScore(submission?.max_score);
  const late = homework?.due_at && isPast(homework.due_at);

  const banner = () => {
    if (!canAnswer) {
      return <div className="hw-take__banner is-info">{tr("take.previewNote")}</div>;
    }
    if (isReviewed) {
      return (
        <div className="hw-take__banner is-done">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          <div>
            <strong>{tr("take.correctedTitle")}</strong>
            <p>{tr("take.correctedBody")}</p>
          </div>
          {score !== null && maxScore ? (
            <div className="hw-take__score">
              <small>{tr("take.scoreLabel")}</small>
              <strong>
                {score} / {maxScore}
              </strong>
            </div>
          ) : null}
        </div>
      );
    }
    if (submission && !editing) {
      return (
        <div className="hw-take__banner is-sent">
          <i className="fa-solid fa-paper-plane" aria-hidden="true" />
          <div>
            <strong>{justSent ? tr("take.successTitle") : tr("take.sentTitle")}</strong>
            <p>
              {justSent
                ? tr("take.successBody")
                : tr("take.sentBody", { date: formatDateTime(submission.submitted_at, language) })}
            </p>
          </div>
          <button type="button" className="btn ghost" onClick={() => setEditing(true)}>
            {tr("take.editAnswers")}
          </button>
        </div>
      );
    }
    if (late) {
      return (
        <div className="hw-take__banner is-late">
          <i className="fa-solid fa-clock" aria-hidden="true" />
          <p>{tr("take.lateWarning")}</p>
        </div>
      );
    }
    return null;
  };

  const content = (
    <main className="test-take hw-take" dir={dir}>
      <div className="container test-take__container" ref={topRef}>
        <Link className="hw-take__back" to="/homeworks">
          <i className="fa-solid fa-arrow-left" aria-hidden="true" /> {tr("take.back")}
        </Link>

        <header className="test-take__header">
          <h1 className="test-take__title">{homework?.title || tr("title")}</h1>
          <div className="test-take__meta">
            {homework?.due_at ? (
              <span>{tr("take.dueLine", { date: formatDateTime(homework.due_at, language) })}</span>
            ) : null}
            {answering && questions.length ? (
              <span>{tr("take.answered", { done: answeredCount, total: questions.length })}</span>
            ) : null}
          </div>
          {answering && questions.length ? (
            <div className="test-take__progress">
              <div className="test-take__progressBar" style={{ width: `${progress}%` }} />
            </div>
          ) : null}
        </header>

        {error ? <div className="test-take__error">{error}</div> : null}
        {loading ? <div className="homeworks-page__spinner" aria-hidden="true" /> : null}

        {!loading && homework ? (
          <>
            {banner()}

            {homework.description ? (
              <section className="test-take__card hw-take__instructions">
                <i className="fa-regular fa-lightbulb" aria-hidden="true" />
                <p>{homework.description}</p>
              </section>
            ) : null}

            {!answering && submission ? (
              <h2 className="hw-take__sectionTitle">{tr("take.yourAnswers")}</h2>
            ) : null}

            <fieldset className="hw-take__fieldset" disabled={!answering && canAnswer}>
              <div className="hw-take__questions">
              {exercises.map((exercise) => {
                const isStimulus = String(exercise.exercise_type || "") === "stimulus";
                return (
                  <section className="test-take__card" key={exercise.id} id={`q-${exercise.id}`}>
                    <div className="test-take__questionHead">
                      <div className="test-take__qIndex">
                        {isStimulus
                          ? typeLabel("stimulus")
                          : t("testsModule.editor.questionN", {
                              defaultValue: "Question {{n}}",
                              n: numberById[String(exercise.id)],
                            })}
                      </div>
                      {!isStimulus ? (
                        <div className="test-take__qMeta">
                          <span className="test-take__qType">{typeLabel(exercise.exercise_type)}</span>
                        </div>
                      ) : null}
                    </div>

                    {isStimulus ? (
                      <div className="test-take__stimulus">
                        <div className="test-take__stimulusBody">{exercise.prompt}</div>
                      </div>
                    ) : exercise.prompt ? (
                      <p className="test-take__prompt">{exercise.prompt}</p>
                    ) : null}

                    {!isStimulus ? (
                      <ExerciseRunner
                        prefix="test-take"
                        language={language}
                        exercise={exercise}
                        value={answers[String(exercise.id)]}
                        onChange={(next) => {
                          setDirty(true);
                          setAnswers((prev) => ({ ...prev, [String(exercise.id)]: next }));
                        }}
                      />
                    ) : null}
                  </section>
                );
              })}
              </div>
            </fieldset>

            {answering && exercises.length ? (
              <div className="test-take__submit hw-take__submit">
                {editing ? (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      setEditing(false);
                      setDirty(false);
                      setAnswers(answersFromSubmission(submission));
                    }}
                    disabled={submitting}
                  >
                    {tr("take.cancelEdit")}
                  </button>
                ) : null}
                <button type="button" className="btn primary" onClick={send} disabled={submitting}>
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                  {submitting ? tr("take.sending") : editing ? tr("take.sendAgain") : tr("take.send")}
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  );

  return isStudent ? <StudentShell>{content}</StudentShell> : content;
}
